import React from "react";

/**
 * ErrorBoundary — catches unhandled render/effect errors in the React tree
 * so the user sees a friendly "Something broke" panel instead of the
 * blank white page that a runtime exception otherwise produces.
 *
 * Wrapped around the recruiter app shell so a crash inside JobDetail or
 * any nested panel doesn't take out the whole page (sidebar stays alive,
 * user can navigate away or click "Reload" without a hard refresh).
 *
 * The error message + stack are exposed (copyable) so the user can share
 * them with support and we can root-cause quickly.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, info);
        this.setState({ info });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, info: null });
    };

    handleReload = () => {
        try {
            window.location.reload();
        } catch {}
    };

    handleCopy = () => {
        try {
            const text = [
                `URL: ${window.location.href}`,
                `Error: ${this.state.error?.message || this.state.error}`,
                `Stack: ${this.state.error?.stack || "n/a"}`,
                `Component stack: ${this.state.info?.componentStack || "n/a"}`,
            ].join("\n\n");
            navigator.clipboard?.writeText(text);
        } catch {}
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        const msg = this.state.error?.message || String(this.state.error || "Unknown error");
        return (
            <div
                data-testid="app-error-boundary"
                className="min-h-[60vh] flex items-center justify-center p-6"
            >
                <div className="max-w-lg w-full border border-red-200 bg-red-50 p-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-700">
                        Something broke on this page
                    </p>
                    <h1 className="mt-2 text-xl font-bold text-ink">
                        We couldn't render this view.
                    </h1>
                    <p className="mt-2 text-sm text-[#525252]">
                        The error is logged. Try reloading — if it happens again,
                        copy the details below and share them with support so we
                        can fix it fast.
                    </p>
                    <pre
                        data-testid="app-error-boundary-message"
                        className="mt-3 text-[11px] font-mono bg-white border border-red-100 p-3 max-h-40 overflow-auto whitespace-pre-wrap break-words"
                    >
                        {msg}
                    </pre>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            data-testid="app-error-boundary-reload"
                            onClick={this.handleReload}
                            className="bg-ink hover:bg-cobalt text-white px-4 py-2 text-xs font-bold tracking-wider uppercase"
                        >
                            Reload page
                        </button>
                        <button
                            data-testid="app-error-boundary-back"
                            onClick={() => window.history.back()}
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-4 py-2 text-xs font-bold tracking-wider uppercase"
                        >
                            Go back
                        </button>
                        <button
                            data-testid="app-error-boundary-copy"
                            onClick={this.handleCopy}
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-4 py-2 text-xs font-bold tracking-wider uppercase"
                        >
                            Copy details
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
